import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { ShoppingCart, Menu, X, User, LogOut, Package, Heart, Search, ChevronDown, ChevronRight, FileCheck2, Info, Beaker, HelpCircle, Truck, Mail, Layers, Home, ShoppingBag } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useWishlist } from "../context/WishlistContext";
import { VortexWordmark, CarbonMark } from "./VortexWordmark";
import api from "../lib/api";

const NAV = [
    { to: "/", label: "Home" },
    { to: "/shop", label: "Shop", hasCategories: true },
    { to: "/stacks/builder", label: "Build a Stack" },
    { to: "/about", label: "About Us" },
    { to: "/lab-standards", label: "Lab Standards" },
    { to: "/coa-library", label: "COA Library" },
    { to: "/faq", label: "FAQ" },
    { to: "/policies/shipping", label: "Shipping" },
    { to: "/contact", label: "Contact" },
];

export const Header = () => {
    const { count } = useCart();
    const { user, logout } = useAuth();
    const { count: wishCount } = useWishlist();
    const navigate = useNavigate();
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [accountOpen, setAccountOpen] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [shopOpen, setShopOpen] = useState(false);
    const [categories, setCategories] = useState([]);
    const accountRef = useRef(null);
    const searchRef = useRef(null);
    const shopRef = useRef(null);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setMobileOpen(false);
        setAccountOpen(false);
        setSearchOpen(false);
        setShopOpen(false);
    }, [location.pathname, location.search]);

    useEffect(() => {
        const onClick = (e) => {
            if (accountRef.current && !accountRef.current.contains(e.target)) setAccountOpen(false);
            if (searchRef.current && !searchRef.current.contains(e.target)) setSearchOpen(false);
            if (shopRef.current && !shopRef.current.contains(e.target)) setShopOpen(false);
        };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, []);

    // Load categories once for the Shop dropdown
    useEffect(() => {
        api.get("/categories").then((r) => setCategories(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    }, []);

    // Debounced predictive product search
    useEffect(() => {
        if (!searchQuery || searchQuery.length < 2) {
            setSearchResults([]);
            return;
        }
        const t = setTimeout(() => {
            api.get("/products", { params: { search: searchQuery } })
                .then((r) => setSearchResults((r.data || []).slice(0, 6)))
                .catch(() => setSearchResults([]));
        }, 200);
        return () => clearTimeout(t);
    }, [searchQuery]);

    const submitSearch = (e) => {
        e.preventDefault();
        const q = searchQuery.trim();
        if (!q) return;
        setSearchOpen(false);
        setSearchQuery("");
        navigate(`/shop?search=${encodeURIComponent(q)}`);
    };

    const handleLogout = async () => {
        await logout();
        setAccountOpen(false);
        navigate("/");
    };

    // Drawer section catalogue — every link resolves to an existing
    // Route in App.js. Keep grouped so navigation reads as a laboratory
    // control panel rather than a flat list.
    const DRAWER_SECTIONS = [
        {
            title: "Shop",
            links: [
                { to: "/", label: "Home", icon: Home },
                { to: "/shop", label: "Products", icon: Package },
                { to: "/shop?filter=new", label: "New Arrivals", icon: Package },
                { to: "/shop?filter=bestseller", label: "Bestsellers", icon: Package },
                { to: "/stacks/builder", label: "Build a Stack", icon: Layers },
                { to: "/stacks", label: "Prebuilt Stacks", icon: Layers },
            ],
        },
        {
            title: "Research",
            links: [
                { to: "/coa-library", label: "COA Library", icon: FileCheck2 },
                { to: "/lab-standards", label: "Lab Standards", icon: Beaker },
                { to: "/reviews", label: "Reviews", icon: Heart },
            ],
        },
        {
            title: "Information",
            links: [
                { to: "/about", label: "About Carbon Labs", icon: Info },
                { to: "/faq", label: "FAQ", icon: HelpCircle },
                { to: "/policies/shipping", label: "Shipping", icon: Truck },
                { to: "/policies/returns", label: "Returns", icon: Truck },
                { to: "/policies/privacy", label: "Privacy Policy", icon: FileCheck2 },
                { to: "/policies/terms", label: "Terms & Conditions", icon: FileCheck2 },
                { to: "/contact", label: "Contact", icon: Mail },
            ],
        },
        {
            title: "Account",
            links: [
                { to: "/account",  label: "My Account",  icon: User,         guard: ({ user }) => !!user },
                { to: "/login",    label: "Sign In",     icon: User,         guard: ({ user }) => !user },
                { to: "/register", label: "Register",    icon: User,         guard: ({ user }) => !user },
                { to: "/wishlist", label: "Wishlist",    icon: Heart,        badge: wishCount },
                { to: "/cart",     label: "Cart",        icon: ShoppingCart, badge: count },
            ],
        },
    ];

    return (
        <header
            data-testid="site-header"
            className={`sticky top-0 z-50 transition-colors duration-300 ${
                scrolled ? "header-scrolled" : "header-solid"
            }`}
        >
            <div className="container-x">
                {/* Row 1 — icon left | wordmark absolutely centred against
                 * the viewport container | controls right. Wordmark uses
                 * `left-1/2 -translate-x-1/2` so it sits on the true
                 * horizontal centre regardless of the icon or the right
                 * controls widths — the wordmark and mascot below therefore
                 * share the same vertical axis. */}
                <div className="relative flex items-center min-h-[92px] md:min-h-[108px] py-2">
                    <Link to="/" className="flex items-center shrink-0 z-10" data-testid="logo-link" aria-label="Carbon Labs — home">
                        <CarbonMark size={40} />
                    </Link>

                    {/* Absolutely-centred brand block */}
                    <Link
                        to="/"
                        className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 flex items-center pointer-events-auto"
                        aria-label="Carbon Labs — home"
                        data-testid="brand-wordmark-link"
                    >
                        <VortexWordmark
                            size="md"
                            variant="static"
                            showIcon={false}
                            showPillars
                        />
                    </Link>

                    {/* Right-side controls */}
                    <div className="ml-auto flex items-center gap-1.5 sm:gap-2 z-10 shrink-0">
                        {/* Desktop-only cluster (mobile uses the utility bar
                         * row further below) */}
                        <div className="hidden lg:flex items-center gap-2">
                        {/* Predictive search */}
                        <div className="relative" ref={searchRef}>
                        <button
                            onClick={() => setSearchOpen((v) => !v)}
                            className="relative p-2 sm:p-2.5 rounded-full border border-white/10 hover:border-[var(--gold)] transition-colors group"
                            aria-label="Search"
                            data-testid="header-search-btn"
                        >
                            <Search size={18} className="text-white group-hover:text-[var(--gold)]" />
                        </button>
                        {searchOpen && (
                            <div
                                className="fixed left-3 right-3 top-[calc(var(--header-h)+8px)] md:absolute md:left-auto md:right-0 md:top-full md:mt-2 md:w-[380px] md:max-w-[90vw] glass-elevated rounded-xl border border-white/10 overflow-hidden z-50 p-3"
                                data-testid="header-search-panel"
                                style={{ animation: "fadeInUp 200ms ease-out" }}
                            >
                                <form onSubmit={submitSearch} data-testid="header-search-form">
                                    <div className="relative">
                                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                                        <input
                                            autoFocus
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            placeholder="Search products…"
                                            className="w-full pl-9 pr-3 py-2.5 bg-black/40 border border-white/10 rounded-full text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--gold)]"
                                            data-testid="header-search-input"
                                        />
                                    </div>
                                </form>
                                {searchQuery.length >= 2 && (
                                    <div className="mt-2 max-h-72 overflow-y-auto" data-testid="header-search-results">
                                        {searchResults.length === 0 ? (
                                            <p className="text-xs text-[var(--text-muted)] px-2 py-3">No matches — try a different search.</p>
                                        ) : (
                                            searchResults.map((p) => (
                                                <Link
                                                    key={p.slug}
                                                    to={`/product/${p.slug}`}
                                                    className="flex items-center gap-3 p-2 rounded-lg hover:bg-[rgba(0,229,255,0.08)]"
                                                    data-testid={`search-result-${p.slug}`}
                                                >
                                                    {p.image && (
                                                        <img src={p.image} alt="" loading="lazy" className="w-14 h-10 rounded-lg object-contain border border-white/10" />
                                                    )}
                                                    <div className="min-w-0 flex-1">
                                                        <p className="text-sm text-white truncate">{p.name}</p>
                                                        <p className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] truncate">{p.category}</p>
                                                    </div>
                                                </Link>
                                            ))
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Wishlist */}
                    <Link
                        to="/wishlist"
                        className="relative p-2 sm:p-2.5 rounded-full border border-white/10 hover:border-[var(--gold)] transition-colors group"
                        aria-label="Wishlist"
                        data-testid="header-wishlist-link"
                    >
                        <Heart
                            size={18}
                            className={wishCount > 0 ? "text-[var(--gold)]" : "text-white group-hover:text-[var(--gold)]"}
                            fill={wishCount > 0 ? "currentColor" : "none"}
                        />
                        {wishCount > 0 && (
                            <span
                                className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[var(--gold)] text-black text-[10px] font-mono font-bold flex items-center justify-center"
                                data-testid="header-wishlist-count"
                            >
                                {wishCount}
                            </span>
                        )}
                    </Link>

                    {/* Account */}
                    <div className="relative" ref={accountRef}>
                        {user ? (
                            <>
                                <button
                                    onClick={() => setAccountOpen((v) => !v)}
                                    className="p-2 sm:p-2.5 rounded-full border border-white/10 hover:border-[var(--gold)] transition-colors group"
                                    aria-label="Account menu"
                                    data-testid="header-account-btn"
                                >
                                    {user.picture ? (
                                        <img src={user.picture} alt="" className="w-[18px] h-[18px] rounded-full" />
                                    ) : (
                                        <User size={18} className="text-white group-hover:text-[var(--gold)]" />
                                    )}
                                </button>
                                {accountOpen && (
                                    <div
                                        className="absolute right-0 top-full mt-2 w-56 glass-elevated rounded-xl border border-white/10 overflow-hidden z-50"
                                        data-testid="header-account-dropdown"
                                        style={{ animation: "fadeInUp 200ms ease-out" }}
                                    >
                                        <div className="px-4 py-3 border-b border-white/5">
                                            <div className="text-xs text-[var(--text-muted)] font-mono uppercase tracking-widest">Signed in as</div>
                                            <div className="text-sm text-white truncate font-medium">{user.email}</div>
                                        </div>
                                        <Link
                                            to="/account"
                                            onClick={() => setAccountOpen(false)}
                                            className="flex items-center gap-2 px-4 py-3 text-sm text-white/85 hover:bg-[rgba(0,229,255,0.08)] hover:text-[var(--gold)]"
                                            data-testid="header-account-link"
                                        >
                                            <Package size={14} /> My orders
                                        </Link>
                                        <button
                                            onClick={handleLogout}
                                            className="w-full flex items-center gap-2 px-4 py-3 text-sm text-white/85 hover:bg-[rgba(0,229,255,0.08)] hover:text-[var(--gold)] border-t border-white/5"
                                            data-testid="header-logout-btn"
                                        >
                                            <LogOut size={14} /> Sign out
                                        </button>
                                    </div>
                                )}
                            </>
                        ) : (
                            <Link
                                to="/login"
                                className="p-2 sm:p-2.5 rounded-full border border-white/10 hover:border-[var(--gold)] transition-colors group"
                                aria-label="Sign in"
                                data-testid="header-login-link"
                            >
                                <User size={18} className="text-white group-hover:text-[var(--gold)]" />
                            </Link>
                        )}
                    </div>

                    <Link
                        to="/cart"
                        data-testid="header-cart-link"
                        className="relative p-2 sm:p-2.5 rounded-full border border-white/10 hover:border-[var(--gold)] transition-colors group"
                        aria-label="Cart"
                    >
                        <ShoppingCart size={18} className="text-white group-hover:text-[var(--gold)]" />
                        {count > 0 && (
                            <span
                                data-testid="cart-count-badge"
                                className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[var(--gold)] text-black text-[10px] font-bold flex items-center justify-center"
                            >
                                {count}
                            </span>
                        )}
                    </Link>
                    </div>
                    {/* Mobile-only hamburger — utility icons live in the
                     * dedicated bar below */}
                    <button
                        className="lg:hidden p-2 sm:p-2.5 rounded-full border border-white/10 text-white"
                        onClick={() => setMobileOpen((v) => !v)}
                        data-testid="mobile-menu-toggle"
                        aria-label="Menu"
                    >
                        {mobileOpen ? <X size={18} /> : <Menu size={18} />}
                    </button>
                </div>
            </div>
            {/* Mobile utility bar — search field + labelled controls.
             * Sits under Row 1 on mobile; hidden on desktop where the
             * pill-icon cluster inside Row 1 handles the same actions. */}
            {/* Mobile utility bar — SHOP pill + labelled utility icons.
             * The Search icon here toggles a full-width expandable panel
             * below (Row 3) so mobile users get a large, focusable input
             * that never gets squeezed by the icons alongside it. */}
            <div className="lg:hidden container-x pb-3 pt-1 flex items-center gap-2">
                <Link
                    to="/shop"
                    className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2.5 rounded-full border border-[var(--cyan)] bg-[rgba(0,229,255,0.10)] text-[var(--cyan-hi)] font-mono uppercase text-[11px] tracking-[0.22em] font-semibold hover:bg-[rgba(0,229,255,0.16)] hover:shadow-[0_0_12px_rgba(0,229,255,0.35)] transition-all"
                    data-testid="mobile-util-shop"
                >
                    <ShoppingBag size={14} strokeWidth={2.4} />
                    Shop
                </Link>
                <div className="flex-1" />
                <button
                    onClick={() => setSearchOpen((v) => !v)}
                    className={`relative flex flex-col items-center justify-center gap-0.5 shrink-0 px-1 transition-colors ${searchOpen ? "text-[var(--cyan)]" : "text-white/80 hover:text-[var(--cyan)]"}`}
                    aria-label="Search products"
                    aria-expanded={searchOpen}
                    data-testid="mobile-util-search"
                >
                    <Search size={18} />
                    <span className="text-[8.5px] font-mono uppercase tracking-[0.18em]">Search</span>
                </button>
                <Link
                    to="/wishlist"
                    className="relative flex flex-col items-center justify-center gap-0.5 shrink-0 px-1 text-white/80 hover:text-[var(--cyan)] transition-colors"
                    aria-label="Wishlist"
                    data-testid="mobile-util-wishlist"
                >
                    <Heart size={18} fill={wishCount > 0 ? "currentColor" : "none"} />
                    <span className="text-[8.5px] font-mono uppercase tracking-[0.18em]">Wishlist</span>
                    {wishCount > 0 && (
                        <span className="absolute -top-1 right-0 min-w-[16px] h-[16px] px-1 rounded-full bg-[var(--cyan)] text-black text-[9px] font-bold flex items-center justify-center">
                            {wishCount}
                        </span>
                    )}
                </Link>
                <Link
                    to={user ? "/account" : "/login"}
                    className="flex flex-col items-center justify-center gap-0.5 shrink-0 px-1 text-white/80 hover:text-[var(--cyan)] transition-colors"
                    aria-label="Account"
                    data-testid="mobile-util-account"
                >
                    <User size={18} />
                    <span className="text-[8.5px] font-mono uppercase tracking-[0.18em]">Account</span>
                </Link>
                <Link
                    to="/cart"
                    className="relative flex flex-col items-center justify-center gap-0.5 shrink-0 px-1 text-white/80 hover:text-[var(--cyan)] transition-colors"
                    aria-label="Cart"
                    data-testid="mobile-util-cart"
                >
                    <ShoppingCart size={18} />
                    <span className="text-[8.5px] font-mono uppercase tracking-[0.18em]">Cart</span>
                    {count > 0 && (
                        <span className="absolute -top-1 right-0 min-w-[16px] h-[16px] px-1 rounded-full bg-[var(--cyan)] text-black text-[9px] font-bold flex items-center justify-center">
                            {count}
                        </span>
                    )}
                </Link>
            </div>

            {/* Mobile search panel — full-width, expands under the utility
             * bar when the Search icon is tapped. Large touch target,
             * autofocus, native keyboard, native form-submit for Enter. */}
            {searchOpen && (
                <div
                    className="lg:hidden container-x pb-3"
                    data-testid="mobile-search-panel"
                    style={{ animation: "fadeInUp 200ms ease-out" }}
                >
                    <form onSubmit={submitSearch} className="flex items-center gap-2">
                        <div className="relative flex-1 min-w-0">
                            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/60 pointer-events-none" />
                            <input
                                autoFocus
                                type="search"
                                enterKeyHint="search"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Search products…"
                                className="w-full pl-11 pr-10 py-3.5 rounded-full bg-black/70 border border-[var(--cyan)] text-base text-white placeholder:text-white/50 font-mono tracking-wide focus:outline-none focus:shadow-[0_0_0_2px_rgba(0,229,255,0.35)] transition-all"
                                data-testid="mobile-search-input"
                            />
                            {searchQuery && (
                                <button
                                    type="button"
                                    onClick={() => setSearchQuery("")}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-white/50 hover:text-white"
                                    aria-label="Clear search"
                                    data-testid="mobile-search-clear"
                                >
                                    <X size={14} />
                                </button>
                            )}
                        </div>
                        <button
                            type="button"
                            onClick={() => { setSearchOpen(false); setSearchQuery(""); }}
                            className="shrink-0 p-3 rounded-full border border-white/15 text-white/70 hover:text-[var(--cyan)] hover:border-[var(--cyan)] transition-colors"
                            aria-label="Close search"
                            data-testid="mobile-search-close"
                        >
                            <X size={16} />
                        </button>
                    </form>
                    {searchQuery.length >= 2 && (
                        <div className="mt-3 max-h-72 overflow-y-auto rounded-2xl border border-[rgba(0,229,255,0.18)] bg-black/85 backdrop-blur-md" data-testid="mobile-search-results">
                            {searchResults.length === 0 ? (
                                <p className="text-xs text-white/50 px-4 py-4 font-mono uppercase tracking-widest">No matches — try a different search.</p>
                            ) : (
                                searchResults.map((p) => (
                                    <Link
                                        key={p.slug}
                                        to={`/product/${p.slug}`}
                                        onClick={() => { setSearchOpen(false); setSearchQuery(""); }}
                                        className="flex items-center gap-3 p-3 border-b border-white/5 hover:bg-[rgba(0,229,255,0.08)]"
                                        data-testid={`mobile-search-result-${p.slug}`}
                                    >
                                        {p.image && <img src={p.image} alt="" loading="lazy" className="w-14 h-10 rounded-md object-contain border border-white/10 shrink-0" />}
                                        <div className="min-w-0 flex-1">
                                            <p className="text-sm text-white truncate">{p.name}</p>
                                            <p className="text-[10px] font-mono uppercase tracking-widest text-white/50 truncate">{p.category}</p>
                                        </div>
                                    </Link>
                                ))
                            )}
                        </div>
                    )}
                </div>
            )}
            {/* Row 2 — desktop horizontal nav rail. Centred under the brand
             * block, out of the way of the wordmark on Row 1. Hidden on
             * mobile (drawer takes over). */}
            <nav className="hidden lg:flex items-center justify-center gap-7 pb-3">
                {NAV.map((item) => {
                    if (item.hasCategories) {
                        return (
                            <div key={item.to + item.label} className="relative" ref={shopRef}>
                                <button
                                    onMouseEnter={() => setShopOpen(true)}
                                    onClick={() => setShopOpen((v) => !v)}
                                    className={`nav-link inline-flex items-center gap-1 ${location.pathname === "/shop" ? "active" : ""}`}
                                    data-testid="nav-shop-dropdown-btn"
                                >
                                    {item.label} <ChevronDown size={12} className={`transition-transform ${shopOpen ? "rotate-180" : ""}`} />
                                </button>
                                {shopOpen && (
                                    <div
                                        onMouseLeave={() => setShopOpen(false)}
                                        className="absolute left-0 top-full mt-2 w-64 glass-elevated rounded-xl border border-white/10 overflow-hidden z-50 p-2"
                                        data-testid="nav-shop-dropdown"
                                        style={{ animation: "fadeInUp 200ms ease-out" }}
                                    >
                                        <Link to="/shop" className="block px-4 py-2.5 rounded-lg text-sm text-white hover:bg-[rgba(0,229,255,0.08)] hover:text-[var(--cyan-hi)]" data-testid="nav-shop-all">
                                            All products
                                        </Link>
                                        <div className="h-px my-1 bg-white/5" />
                                        {categories.map((c) => (
                                            <Link
                                                key={c.slug}
                                                to={`/shop?category=${c.slug}`}
                                                className="block px-4 py-2.5 rounded-lg text-sm text-white/80 hover:bg-[rgba(0,229,255,0.08)] hover:text-[var(--cyan-hi)]"
                                                data-testid={`nav-shop-cat-${c.slug}`}
                                            >
                                                {c.name}
                                            </Link>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    }
                    return (
                        <NavLink
                            key={item.to + item.label}
                            to={item.to}
                            end={item.to === "/"}
                            className={({ isActive }) =>
                                `nav-link ${isActive && item.to === location.pathname + location.search ? "active" : ""}`
                            }
                            data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                        >
                            {item.label}
                        </NavLink>
                    );
                })}
            </nav>
            </div>

            {/* Mobile menu — Carbon Labs navigation drawer.
             * Portalled to <body> so it escapes the sticky header's
             * containing block (which backdrop-filter otherwise clamps). */}
            {mobileOpen && createPortal(
                <>
                    <div
                        className="lg:hidden fixed inset-0 bg-black/70 backdrop-blur-sm z-[100]"
                        onClick={() => setMobileOpen(false)}
                        data-testid="mobile-menu-backdrop"
                    />
                    <aside
                        className="lg:hidden fixed right-0 top-0 bottom-0 w-[86%] max-w-sm z-[101] cfibre-panel border-l border-[rgba(0,229,255,0.28)] flex flex-col"
                        data-testid="mobile-menu"
                        role="dialog"
                        aria-label="Site navigation"
                        style={{ animation: "slideInRight 280ms cubic-bezier(0.2,0.8,0.2,1)" }}
                    >
                        {/* Header */}
                        <div className="flex items-start justify-between p-5 border-b border-[rgba(0,229,255,0.18)]">
                            <Link to="/" onClick={() => setMobileOpen(false)} className="block" aria-label="Carbon Labs — home">
                                <VortexWordmark size="sm" variant="static" showPillars />
                            </Link>
                            <button
                                onClick={() => setMobileOpen(false)}
                                className="p-2 -mr-1 text-white/80 hover:text-[var(--cyan)] transition-colors"
                                aria-label="Close menu"
                                data-testid="mobile-menu-close"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        {/* Scrollable body */}
                        <div className="flex-1 overflow-y-auto py-4 px-1">
                            {DRAWER_SECTIONS.map((section) => (
                                <div key={section.title} className="mb-4 px-4">
                                    <div className="flex items-center gap-2 mb-2 pl-1">
                                        <span className="w-3 h-px bg-[var(--cyan)] shadow-[0_0_4px_rgba(0,229,255,0.7)]" />
                                        <span
                                            className="text-[10px] font-mono uppercase tracking-[0.28em] text-[var(--cyan-hi)]"
                                            data-testid={`drawer-section-${section.title.toLowerCase()}`}
                                        >
                                            {section.title}
                                        </span>
                                    </div>
                                    <ul className="space-y-0.5">
                                        {section.links
                                            .filter((l) => !l.guard || l.guard({ user }))
                                            .map((l) => (
                                                <li key={l.to}>
                                                    <NavLink
                                                        to={l.to}
                                                        end={l.to === "/"}
                                                        onClick={() => setMobileOpen(false)}
                                                        className={({ isActive }) =>
                                                            `flex items-center gap-3 pl-3 pr-2 py-2.5 rounded-sm transition-all border-l-2 ${
                                                                isActive
                                                                    ? "border-[var(--cyan)] bg-[rgba(0,229,255,0.06)] text-[var(--cyan-hi)]"
                                                                    : "border-transparent text-white/85 hover:text-[var(--cyan-hi)] hover:bg-[rgba(0,229,255,0.05)] hover:border-[rgba(0,229,255,0.4)]"
                                                            }`
                                                        }
                                                        data-testid={`drawer-link-${l.to.replace(/\W+/g, "-")}`}
                                                    >
                                                        <l.icon size={15} strokeWidth={2} className="shrink-0" />
                                                        <span className="flex-1 text-[13px] font-display font-semibold uppercase tracking-[0.14em]">
                                                            {l.label}
                                                        </span>
                                                        {l.badge != null && Number(l.badge) > 0 && (
                                                            <span className="inline-flex items-center justify-center min-w-[20px] h-[20px] px-1.5 rounded-full bg-[var(--cyan)] text-black text-[10px] font-bold">
                                                                {l.badge}
                                                            </span>
                                                        )}
                                                        <ChevronRight size={13} className="text-white/40 shrink-0" />
                                                    </NavLink>
                                                </li>
                                            ))}
                                    </ul>
                                </div>
                            ))}

                            {/* Session actions — sign-out only surfaces for
                             * authenticated users; guests see Login link
                             * in the Account section above. */}
                            {user && (
                                <div className="mb-4 px-4">
                                    <div className="flex items-center gap-2 mb-2 pl-1">
                                        <span className="w-3 h-px bg-[var(--cyan)]" />
                                        <span className="text-[10px] font-mono uppercase tracking-[0.28em] text-[var(--cyan-hi)]">
                                            Session
                                        </span>
                                    </div>
                                    <div className="pl-3 mb-2 text-[10px] font-mono text-white/50 truncate">
                                        {user.email}
                                    </div>
                                    <button
                                        onClick={() => {
                                            handleLogout();
                                            setMobileOpen(false);
                                        }}
                                        className="flex w-full items-center gap-3 pl-3 pr-2 py-2.5 rounded-sm border-l-2 border-transparent text-white/85 hover:text-red-300 hover:bg-red-500/10 hover:border-red-400/50 transition-all"
                                        data-testid="drawer-signout"
                                    >
                                        <LogOut size={15} strokeWidth={2} />
                                        <span className="flex-1 text-left text-[13px] font-display font-semibold uppercase tracking-[0.14em]">
                                            Sign Out
                                        </span>
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="px-5 py-4 border-t border-[rgba(0,229,255,0.18)] flex flex-col gap-2 bg-black/40">
                            <a
                                href="mailto:carbonlabsau@outlook.com"
                                className="text-[10px] font-mono uppercase tracking-[0.22em] text-white/60 hover:text-[var(--cyan)] transition-colors truncate"
                                data-testid="drawer-email"
                            >
                                carbonlabsau@outlook.com
                            </a>
                            <p className="text-[9px] font-mono uppercase tracking-[0.22em] text-white/35">
                                © {new Date().getFullYear()} Carbon Labs · Sydney AU
                            </p>
                        </div>
                    </aside>
                </>,
                document.body
            )}
        </header>
    );
};
